import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import dayjs from 'dayjs'
import { AddIcon, DeleteIcon } from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  deletePendingNewJersey,
  deletePendingNewJerseyCourse,
  deleteNewJersey,
  deleteNewJerseyCourse,
  finalizePendingNewJersey,
  finalizePendingNewJerseyCourse,
  clearNewJerseyOperationStatus,
  selectNewJerseyOperationStatus,
  selectNewJerseyOperationError,
  selectNewJerseyOperationSuccess,
} from 'src/v3/content-pipeline/accreditation.slice'
import ConfirmAction from 'src/v3/components/confirm-action'
import {
  NewJerseyPendingAccreditation,
  NewJerseyFinalizedAccreditation,
} from './types'
import { CreateForm } from './create-form'
import { EditForm } from './edit-form'
import { FieldComparison, StatusActions, AutoTrackedDates } from '../shared'
import {
  getStatusColor,
  getAccreditationFlags,
  RejectedAlert,
  ApprovedAlert,
  OperationSnackbars,
  calculateFieldChanges,
} from '../../shared-utils'

type NewJerseySectionProps = {
  eventId?: string
  courseId?: string
  finalized?: NewJerseyFinalizedAccreditation
  pending?: NewJerseyPendingAccreditation
}

export let NewJerseySection = ({
  eventId,
  courseId,
  finalized,
  pending,
}: NewJerseySectionProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [formOpen, setFormOpen] = useState(false)
  let [editFormOpen, setEditFormOpen] = useState(false)
  let [expanded, setExpanded] = useState(false)
  let [confirm, setConfirm] = useState('')

  let operationStatus = useAppSelector(selectNewJerseyOperationStatus)
  let operationError = useAppSelector(selectNewJerseyOperationError)
  let operationSuccess = useAppSelector(selectNewJerseyOperationSuccess)

  let submitting = operationStatus === 'pending'
  let { hasNoAccreditation, hasOnlyFinalized, hasOnlyPending, hasBoth } =
    getAccreditationFlags(finalized, pending)
  let statusColor = getStatusColor(pending?.status)

  let handleFinalize = () => {
    if (!pending?.id) return
    if (courseId) {
      dispatch(finalizePendingNewJerseyCourse({ id: pending.id, courseId }))
    } else if (eventId) {
      dispatch(finalizePendingNewJersey({ id: pending.id, eventId }))
    }
  }

  let changes = calculateFieldChanges(finalized, pending, [
    { key: 'courseCode', label: 'Course Code' },
    { key: 'expirationDate', label: 'Expiration Date' },
  ])

  return (
    <Box>
      <Card variant="outlined" sx={{ overflow: 'visible' }}>
        <Box className="p-4 grid grid-cols-[1fr_auto] gap-4 items-center">
          <Box className="flex items-center gap-4">
            <Typography variant="h6">New Jersey Accreditation</Typography>
            {finalized && (
              <>
                <Chip color="success" label="Finalized" size="small" />
                <Typography color="text.secondary" variant="body2">
                  {finalized.courseCode}
                </Typography>
              </>
            )}
            {hasOnlyPending && pending && (
              <>
                <Chip
                  label={pending.status}
                  size="small"
                  sx={{
                    color: statusColor,
                  }}
                />
                <Typography color="text.secondary" variant="body2">
                  {pending.courseCode || 'Pending'}
                </Typography>
              </>
            )}
            {hasNoAccreditation && (
              <Chip color="default" label="Not Created" size="small" />
            )}
          </Box>

          <Box className="flex items-center gap-2">
            {hasBoth && pending && changes.length > 0 && (
              <Chip
                color="warning"
                label={`${changes.length} ${changes.length === 1 ? 'Change' : 'Changes'}`}
                size="small"
              />
            )}
            {hasBoth && pending && (
              <Chip
                label={pending.status}
                size="small"
                sx={{
                  color: statusColor,
                }}
              />
            )}
            {(hasNoAccreditation || hasOnlyFinalized) && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                size="small"
                onClick={() => setFormOpen(true)}
              >
                {hasNoAccreditation ? 'Create' : 'New Update'}
              </Button>
            )}
            {(finalized || pending) && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Show less' : 'Show more'}
              >
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        {pending?.status === 'Rejected' && (
          <RejectedAlert
            rejectedDate={pending.rejectedDate}
            notes={pending.notes}
          />
        )}

        {pending?.status === 'Approved' && (
          <ApprovedAlert approvedDate={pending.approvedDate} />
        )}

        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            {hasNoAccreditation && (
              <Typography color="text.secondary" variant="body2">
                No New Jersey accreditation has been created for this{' '}
                {courseId ? 'course' : 'event'}.
              </Typography>
            )}

            {hasBoth && finalized && pending && (
              <Box>
                <Box className="grid grid-cols-2 gap-4 mb-4">
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Current (Finalized)
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    Pending Update
                  </Typography>
                </Box>

                <FieldComparison
                  label="Course Code"
                  finalizedValue={finalized.courseCode}
                  pendingValue={pending.courseCode}
                />
                <FieldComparison
                  label="Expiration Date"
                  finalizedValue={finalized.expirationDate ?? ''}
                  pendingValue={pending.expirationDate}
                  isDate
                />

                {pending.notes && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paperDark' }}>
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Notes
                    </Typography>
                    <Typography variant="body2">{pending.notes}</Typography>
                  </Box>
                )}

                <StatusActions
                  status={pending.status}
                  submitting={submitting}
                  onEdit={() => setEditFormOpen(true)}
                  onDelete={() => setConfirm('delete-pending-new-jersey')}
                  onFinalize={handleFinalize}
                />
              </Box>
            )}

            {hasOnlyFinalized && finalized && (
              <Box>
                <Box className="grid gap-3">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Course Code
                    </Typography>
                    <Typography variant="body2">
                      {finalized.courseCode}
                    </Typography>
                  </Box>
                  {finalized.expirationDate && (
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Expiration Date
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(finalized.expirationDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box className="flex justify-end mt-3">
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setConfirm('delete-finalized-new-jersey')}
                    disabled={submitting}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            )}

            {hasOnlyPending && pending && (
              <Box>
                <Box className="grid gap-3">
                  {pending.courseCode && (
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Course Code
                      </Typography>
                      <Typography variant="body2">
                        {pending.courseCode}
                      </Typography>
                    </Box>
                  )}
                  {pending.expirationDate && (
                    <Box>
                      <Typography color="text.secondary" variant="caption">
                        Expiration Date
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(pending.expirationDate).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  )}
                  <AutoTrackedDates
                    submittedDate={pending.submittedDate}
                    approvedDate={pending.approvedDate}
                    rejectedDate={pending.rejectedDate}
                  />
                  {pending.notes && (
                    <Box sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                      <Typography
                        color="text.secondary"
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        Notes
                      </Typography>
                      <Typography variant="body2">{pending.notes}</Typography>
                    </Box>
                  )}
                </Box>

                <StatusActions
                  status={pending.status}
                  submitting={submitting}
                  onEdit={() => setEditFormOpen(true)}
                  onDelete={() => setConfirm('delete-pending-new-jersey')}
                  onFinalize={handleFinalize}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Card>
      <CreateForm
        eventId={eventId}
        courseId={courseId}
        open={formOpen}
        setOpen={setFormOpen}
        onSuccess={() => {}}
      />
      {pending && (
        <EditForm
          eventId={eventId}
          courseId={courseId}
          pending={pending}
          finalized={finalized}
          open={editFormOpen}
          setOpen={setEditFormOpen}
          onSuccess={() => {}}
        />
      )}
      {pending && (
        <ConfirmAction
          open={confirm}
          setOpen={setConfirm}
          openText="delete-pending-new-jersey"
          title="Delete Pending New Jersey?"
          text="Are you sure you want to delete this pending New Jersey accreditation? This action cannot be undone."
          confirmText="Delete"
          executeAction={async () => {
            if (courseId) {
              dispatch(
                deletePendingNewJerseyCourse({ id: pending.id, courseId }),
              )
            } else if (eventId) {
              dispatch(deletePendingNewJersey({ id: pending.id, eventId }))
            }
          }}
        />
      )}
      {finalized && (
        <ConfirmAction
          open={confirm}
          setOpen={setConfirm}
          openText="delete-finalized-new-jersey"
          title="Delete New Jersey Accreditation?"
          text="Are you sure you want to delete this New Jersey accreditation? This action cannot be undone."
          confirmText="Delete"
          executeAction={async () => {
            if (courseId) {
              dispatch(deleteNewJerseyCourse({ id: finalized.id, courseId }))
            } else if (eventId) {
              dispatch(deleteNewJersey({ id: finalized.id, eventId }))
            }
          }}
        />
      )}
      <OperationSnackbars
        successMessage={operationSuccess}
        errorMessage={operationError}
        onClear={() => dispatch(clearNewJerseyOperationStatus())}
      />
    </Box>
  )
}
